import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material";
import { StyledCard } from "../common/StyledCard";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CampaignIcon from "@mui/icons-material/Campaign";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import StatusChip from "../common/StatusChip";
import { AnnouncementSummaryDto } from "../../shared/dtos/announcement/AnnouncementSummaryDto";

interface AnnouncementCardProps {
  announcement: AnnouncementSummaryDto;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  announcement,
  onView,
  onEdit,
  onDelete,
}) => {
  const isExpired = announcement.expiryDate
    ? new Date(announcement.expiryDate) < new Date()
    : false;

  return (
    <StyledCard>
      <Box
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box sx={{ maxWidth: "70%" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {announcement.title}
            </Typography>
            <Stack direction="row" spacing={1}>
              <StatusChip
                status={announcement.audience}
                icon={<CampaignIcon fontSize="small" />}
              />
              {isExpired && <StatusChip status="EXPIRED" />}
            </Stack>
          </Box>
          <Stack direction="row">
            {onView && (
              <Tooltip title="Read Full Announcement">
                <IconButton size="small" color="info" onClick={onView}>
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ flexGrow: 1, mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {announcement.content}
          </Typography>
        </Box>

        <Stack spacing={1}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              By {announcement.authorName}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Pub:{" "}
              {announcement.publishDate
                ? new Date(announcement.publishDate).toLocaleDateString()
                : "Immediate"}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </StyledCard>
  );
};
export default AnnouncementCard;
